import { memo, type FC } from 'react';
import { Card } from 'antd';
import type { Camera } from '@smolt/shared/types';
import { SessionDisplay } from '@smolt/shared/models';
import { CameraCardHeader } from './CameraCardHeader';
import { CameraCardContent } from './CameraCardContent';
import { CameraCardFooterWrapper } from './CameraCardFooterWrapper';
import { useSlidingWindowRate } from '@/features/counter/hooks/use-sliding-window-rate';

export interface CameraCardProps {
  camera: Camera;
  onSettingsClick: () => void;
  onStartStop: () => void;
  onCardClick?: () => void;
  isMobile?: boolean;
}

/**
 * CameraCard - Main card component
 * Memoized to prevent unnecessary re-renders when camera object identity is unchanged
 * (leverages React Query's structural sharing)
 */
export const CameraCard: FC<CameraCardProps> = memo(
  ({ camera, onSettingsClick, onStartStop, onCardClick, isMobile = false }) => {
    // Convert camera to SessionDisplay for business logic
    const sessionDisplay = new SessionDisplay(camera);

    // Extract primitive values for child components
    const statusText = sessionDisplay.getStatusText();
    const isCounting = sessionDisplay.isCounting;
    const shouldShowCountingIndicator = sessionDisplay.shouldShowCountingIndicator;

    // Calculate weight
    const weight = ((camera.weightPerFish ?? 0) * camera.count) / 1000;

    // Calculate real-time speed using sliding window (5-second window)
    const slidingWindowRates = useSlidingWindowRate(camera.count, weight, isCounting, {
      windowSizeSeconds: 5,
    });

    return (
      <Card
        hoverable={!!onCardClick}
        onClick={onCardClick}
        style={{
          borderRadius: isMobile ? '16px' : '12px',
          boxShadow: isMobile
            ? '0 4px 12px 0 rgba(0, 0, 0, 0.15)'
            : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease',
          cursor: onCardClick ? 'pointer' : 'default',
          position: 'relative',
          backgroundColor: '#FFFFFF',
          width: isMobile ? 'auto' : '100%',
          height: isMobile ? 'calc(100% - 8px)' : '100%',
          maxHeight: isMobile ? '100%' : 'none',
          aspectRatio: '176 / 336',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          containerType: 'inline-size',
        }}
        styles={{
          body: {
            padding: 0,
            backgroundColor: '#FFFFFF',
            borderRadius: '0',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header Component - Memoized */}
        <CameraCardHeader
          cameraName={camera.name}
          shouldShowCountingIndicator={shouldShowCountingIndicator}
          isMobile={isMobile}
        />

        {/* Content Component - Memoized */}
        <div
          style={{
            padding: '3.41% 9.09% 9.09% 9.09%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <CameraCardContent
            statusText={statusText}
            weight={weight}
            weightPerFish={camera.weightPerFish}
            duration={camera.duration}
            isCounting={isCounting}
            tonnesPerHour={slidingWindowRates.tonnesPerHour}
            fishPerHour={slidingWindowRates.fishPerHour}
            inputTank={camera.inputTank}
            outputTank={camera.outputTank}
            isMobile={isMobile}
          />

          {/* Footer Wrapper - Memoized to prevent re-render during counting */}
          <CameraCardFooterWrapper
            cameraState={camera.cameraState}
            sessionState={camera.sessionState}
            onSettingsClick={onSettingsClick}
            onActionClick={onStartStop}
            isMobile={isMobile}
          />
        </div>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if camera object identity changed or handlers changed
    // React Query's structural sharing ensures camera object reference is stable when data hasn't changed
    return (
      prevProps.camera === nextProps.camera &&
      prevProps.onSettingsClick === nextProps.onSettingsClick &&
      prevProps.onStartStop === nextProps.onStartStop &&
      prevProps.onCardClick === nextProps.onCardClick &&
      prevProps.isMobile === nextProps.isMobile
    );
  }
);

CameraCard.displayName = 'CameraCard';
